import { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  ButtonLink,
  Column,
  ContentPage,
  DescriptionList,
  Notice,
  PadlockIcon,
  Row,
  SelectorPageStatus,
  Skeleton,
} from "@ficatranquilo/contabilidade.com-ui";
import { getHourMarkingModelLabel, getHourTrackingModeLabel, getInvoiceDeadlineLabel, getPaymentRuleLabel, getValueInputDeadlineLabel } from "@helpers";
import { closingDayLabel } from "@helpers/competencePeriod";
import { DateText, DomainStatus, Hours, Money, escapeHtml, formatDate } from "@components/ow";

// Partes do detalhe do contrato que as visões da empresa (/c/contracts/:id) e do
// prestador (/e/contracts/:id) compartilham: status, rótulos, blocos e avisos da aba
// Dados, esqueleto de carregamento e as abas. Modelo Detalhe do DS OneWork.

export const CONTRACT_STATUS = { ACTIVE: "ativo", PAUSED: "pausado", TERMINATED: "encerrado", DRAFT: "rascunho" };

// Os mesmos rótulos da lista de contratos.
const REMUNERATION_LABELS = {
  FIXED: "Valor fixo",
  VARIABLE: "Valor lançado pela empresa",
  HOURLY: "Por hora",
  CLT: "CLT · marcação de ponto",
};

export function remunerationLabel(type) {
  return REMUNERATION_LABELS[type] || type || "—";
}

const FIXED_MODE = "FIXED_WITH_HOUR_ADJUSTMENT";

/** Por hora com valor fixo e ajuste: a base é o fixo mensal, a hora só ajusta a diferença da jornada. */
export function isFixedWithAdjustment(contract) {
  return contract?.remuneration_type === "HOURLY" && contract?.hour_tracking_mode === FIXED_MODE;
}

/**
 * Rótulo da remuneração fora da aba Dados (listas e cartões): o do tipo, ou
 * "Valor fixo com ajuste por horas" (o rótulo do Acompanhamento) nesse modo do por hora.
 */
export function remunerationSummary(contract) {
  return isFixedWithAdjustment(contract) ? getHourTrackingModeLabel(FIXED_MODE) : remunerationLabel(contract?.remuneration_type);
}

/** Título do detalhe no Header: "Nome · Projeto", ou só o nome sem projeto. */
export function detailTitle(name, contract) {
  const base = name || "Contrato";
  return contract?.project?.name ? `${base} · ${contract.project.name}` : base;
}

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

const Blocks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

/** Link de texto da lib (projeto, padrão) que navega sem recarregar a página. */
export function DetailLink({ to, text }) {
  const navigate = useNavigate();
  return <ButtonLink $text={text} $action={() => navigate(to)} $noPadding $noWidth />;
}

/** Remuneração: tipo e os campos de cada tipo, como na tela antiga. */
export function RemunerationSection({ contract }) {
  const type = contract.remuneration_type;
  const items = [{ label: "Tipo", value: remunerationLabel(type) }];

  if (type === "FIXED") {
    items.push({ label: "Valor mensal", value: <Money value={contract.fixed_value} /> });
  }
  if (type === "HOURLY") {
    items.push(
      { label: "Valor por hora", value: <Money value={contract.hourly_rate} /> },
      { label: "Modelo de marcação", value: getHourMarkingModelLabel(contract.hour_marking_model) },
      { label: "Acompanhamento", value: getHourTrackingModeLabel(contract.hour_tracking_mode) }
    );
    if (isFixedWithAdjustment(contract)) {
      items.push(
        { label: "Valor fixo mensal", value: <Money value={contract.fixed_value} /> },
        { label: "Horas por dia", value: <Hours hours={Number(contract.expected_daily_hours) || 8} /> }
      );
    }
  }
  if (type === "VARIABLE" && contract.value_input_deadline) {
    items.push({
      label: "Prazo para informar o valor",
      value: getValueInputDeadlineLabel(contract.value_input_deadline, contract.value_input_deadline_month),
    });
  }

  return <DescriptionList $title="Remuneração" $items={items} />;
}

/** Vigência e projeto, com o status do contrato. `project` é o nome já pronto (texto ou link). */
export function TermSection({ contract, project }) {
  const items = [
    { label: "Status", value: <DomainStatus domain="contrato" value={CONTRACT_STATUS[contract.status] || contract.status} /> },
    { label: "Início", value: contract.start_date ? <DateText value={contract.start_date} /> : null },
    { label: "Renovação", value: contract.renewal_date ? <DateText value={contract.renewal_date} /> : null },
  ];
  if (contract.project) items.push({ label: "Projeto", value: project || contract.project.name });
  return <DescriptionList $title="Vigência e projeto" $items={items} />;
}

/**
 * Prazos que vêm do padrão de contrato. `title` muda por visão ("Padrão de contrato"
 * na empresa, "Prazos" no prestador); `pattern` (opcional) é o nome do padrão, texto ou link.
 */
export function PatternSection({ contract, title, pattern }) {
  const cp = contract.contract_pattern || {};
  const items = [];
  if (pattern) items.push({ label: "Padrão", value: pattern });
  items.push(
    {
      label: "Prazo da nota fiscal",
      value: cp.invoice_deadline ? getInvoiceDeadlineLabel(cp.invoice_deadline_type, cp.invoice_deadline, cp.invoice_deadline_month) : null,
    },
    { label: "Regra de pagamento", value: getPaymentRuleLabel(cp.payment_rule_type, cp.payment_rule_value, cp.payment_rule_month) }
  );
  if (contract.remuneration_type === "HOURLY") {
    items.push({ label: "Apuração de horas", value: capitalize(closingDayLabel(cp.closing_day)) });
  }
  return <DescriptionList $title={title} $columns={1} $items={items.map((it) => (it.value === "-" ? { ...it, value: null } : it))} />;
}

/** Códigos de serviço e CNAEs (códigos curtos, separados por vírgula). Some sem nenhum. */
export function ServicesSection({ serviceCodes, cnaes }) {
  const items = [];
  if (Array.isArray(serviceCodes) && serviceCodes.length) items.push({ label: "Códigos de serviço aceitos", value: serviceCodes.join(", ") });
  if (Array.isArray(cnaes) && cnaes.length) items.push({ label: "CNAEs do prestador", value: cnaes.join(", ") });
  if (!items.length) return null;
  return <DescriptionList $title="Serviços" $columns={1} $items={items} />;
}

/** Avisos do topo da aba Dados: contrato encerrado e a regra do valor fixo com ajuste. */
function ContractNotices({ contract }) {
  return (
    <>
      {contract.status === "TERMINATED" ? (
        <Notice
          $variant="gray"
          $icon={<PadlockIcon />}
          $title={contract.terminated_at ? `Contrato encerrado em ${formatDate(contract.terminated_at)}` : "Contrato encerrado"}
          $text="Motivo: %s"
          $replaces={[escapeHtml(contract.termination_reason || "não informado")]}
        />
      ) : null}
      {isFixedWithAdjustment(contract) ? (
        <Notice
          $variant="blue"
          $title="Como o valor é calculado"
          $text="Valor fixo + (horas trabalhadas − jornada esperada do mês) × valor da hora. Feriados nacionais e folgas da empresa já entram na jornada esperada. Nos meses de entrada e saída o fixo é proporcional aos dias úteis. O valor nunca fica negativo."
        />
      ) : null}
    </>
  );
}

/** Aba Dados: avisos no topo e os blocos em duas colunas (`left` e `right`). */
export function DataTab({ contract, left, right }) {
  return (
    <ContentPage>
      <ContractNotices contract={contract} />
      <Row $gap={20} $alignItems="flex-start">
        <Column $columns={6}>
          <Blocks>{left}</Blocks>
        </Column>
        <Column $columns={6}>
          <Blocks>{right}</Blocks>
        </Column>
      </Row>
    </ContentPage>
  );
}

/** Esqueleto do detalhe enquanto o contrato carrega. */
export function ContractDetailSkeleton() {
  return (
    <ContentPage aria-busy="true">
      <Row $justifyContent="space-between" $alignItems="center">
        <Skeleton $width={420} $height={44} $noFlex />
        <Skeleton $width={320} $height={44} $noFlex />
      </Row>
      <Row $gap={20} $alignItems="flex-start">
        <Column $columns={6}>
          <Blocks>
            <Skeleton $height={200} />
            <Skeleton $height={160} />
          </Blocks>
        </Column>
        <Column $columns={6}>
          <Blocks>
            <Skeleton $height={160} />
            <Skeleton $height={200} />
          </Blocks>
        </Column>
      </Row>
    </ContentPage>
  );
}

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Panel = styled.div`
  min-width: 0;
`;

/**
 * Abas do detalhe (segmentadas) com as ações da página à direita. Cada painel só
 * monta na primeira visita e depois fica montado (escondido), para a aba não buscar
 * de novo nem perder o filtro ao trocar. `tabs`: [{ value, label, content }].
 */
export function DetailTabs({ tabs, value, onChange, actions }) {
  const [visited, setVisited] = useState(() => new Set([value]));
  const current = tabs.some((t) => t.value === value) ? value : tabs[0] && tabs[0].value;
  const options = tabs.map(({ value: v, label }) => ({ value: v, label }));

  function change(option) {
    setVisited((prev) => new Set(prev).add(option.value));
    onChange(option.value);
  }

  return (
    <ContentPage>
      <Row $justifyContent="space-between" $alignItems="center" $gap={16}>
        <SelectorPageStatus $variant="segmented" options={options} selected={options.find((o) => o.value === current)} onChange={change} />
        {actions ? <Actions>{actions}</Actions> : null}
      </Row>
      {tabs.map((tab) =>
        visited.has(tab.value) || tab.value === current ? (
          <Panel key={tab.value} role="tabpanel" aria-label={tab.label} hidden={tab.value !== current}>
            {tab.content}
          </Panel>
        ) : null
      )}
    </ContentPage>
  );
}
